<script setup>
import { reactive, computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import NameAndCsvStep from './NameAndCsvStep.vue';
import InboxAndTemplateStep from './InboxAndTemplateStep.vue';
import MediaUploadStep from './MediaUploadStep.vue';
import VariableMappingStep from './VariableMappingStep.vue';
import ReviewAndScheduleStep from './ReviewAndScheduleStep.vue';
import CsvErrorsStep from './CsvErrorsStep.vue';

import {
  MEDIA_FORMATS,
  findComponentByType,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  savedFormState: {
    type: Object,
    default: null,
  },
  lastStep: {
    type: Number,
    default: null,
  },
  csvErrors: {
    type: Object,
    default: null,
  },
  validateCsvFile: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits([
  'submit',
  'csvFileSelected',
  'mediaFileSelected',
  'cancel',
]);

const { t } = useI18n();

const STEPS = {
  NAME_AND_CSV: 0,
  INBOX_AND_TEMPLATE: 1,
  MEDIA_UPLOAD: 2,
  VARIABLE_MAPPING: 3,
  REVIEW_AND_SCHEDULE: 4,
  CSV_ERRORS: 5,
};

const currentStep = ref(
  props.lastStep !== null ? props.lastStep : STEPS.NAME_AND_CSV
);

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  teams: useMapGetter('teams/getTeams'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  teamId: null,
  scheduledAt: null,
  selectedAudience: [],
  useTemplate: false,
  templateParams: null,
  useCSV: true,
};

const state = reactive({ ...initialState });
const selectedWaTemplate = ref(null);
const csvFile = ref(null);
const csvFileName = ref('');
const csvHeaders = ref([]);
const templateVariables = ref([]);
const variableMappings = ref({});
const mediaFile = ref(null);
const mediaFileName = ref('');

const nameAndCsvRules = {
  title: { required, minLength: minLength(1) },
};

const inboxAndTemplateRules = {
  inboxId: { required },
};

const reviewAndScheduleRules = {
  scheduledAt: { required },
};

const nameAndCsvValidator = useVuelidate(nameAndCsvRules, state);
const inboxAndTemplateValidator = useVuelidate(inboxAndTemplateRules, state);
const reviewAndScheduleValidator = useVuelidate(reviewAndScheduleRules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const teamOptions = computed(() =>
  mapToOptions(formState.teams.value, 'id', 'name')
);

const getErrorMessage = (field, validator, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM';
  return validator[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', nameAndCsvValidator.value, 'TITLE'),
  inbox: getErrorMessage('inboxId', inboxAndTemplateValidator.value, 'INBOX'),
  scheduledAt: getErrorMessage(
    'scheduledAt',
    reviewAndScheduleValidator.value,
    'SCHEDULED_AT'
  ),
}));

const hasMediaHeader = computed(() => {
  if (!selectedWaTemplate.value) return false;
  const header = findComponentByType(
    selectedWaTemplate.value,
    COMPONENT_TYPES.HEADER
  );
  return header && MEDIA_FORMATS.includes(header.format);
});

const getMediaHeaderFormat = computed(() => {
  if (!hasMediaHeader.value) return null;
  const header = findComponentByType(
    selectedWaTemplate.value,
    COMPONENT_TYPES.HEADER
  );
  return header.format;
});

const activeSteps = computed(() => {
  const steps = [STEPS.NAME_AND_CSV, STEPS.INBOX_AND_TEMPLATE];

  if (hasMediaHeader.value) {
    steps.push(STEPS.MEDIA_UPLOAD);
  }

  steps.push(STEPS.VARIABLE_MAPPING);
  steps.push(STEPS.REVIEW_AND_SCHEDULE);

  return steps;
});

const currentStepIndex = computed(() => {
  return activeSteps.value.indexOf(currentStep.value);
});

const isCurrentStep = step => currentStep.value === step;
const canNavigateBack = () => currentStepIndex.value > 0;
const canNavigateForward = () => {
  const currentIndex = currentStepIndex.value;
  return currentIndex >= 0 && currentIndex < activeSteps.value.length - 1;
};

const totalSteps = computed(() => {
  if (isCurrentStep(STEPS.CSV_ERRORS)) {
    return activeSteps.value.length + 1;
  }
  return activeSteps.value.length;
});

const formatToUTCString = localDateTime => {
  if (!localDateTime) return null;
  return new Date(localDateTime).toISOString();
};

const handleBack = () => {
  if (!canNavigateBack()) return;

  const currentIndex = currentStepIndex.value;
  currentStep.value = activeSteps.value[currentIndex - 1];
};

const prepareCampaignData = () => {
  const hasAnyMappings = Object.keys(variableMappings.value).length > 0;

  const campaignDetails = {
    title: state.title,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
  };

  if (state.teamId) {
    campaignDetails.team_id = state.teamId;
  }

  if (state.useTemplate && selectedWaTemplate.value) {
    const bodyComponent = selectedWaTemplate.value.components?.find(
      c => c.type === 'BODY'
    );
    campaignDetails.message = bodyComponent?.text || '';

    campaignDetails.template_info = JSON.stringify({
      name: selectedWaTemplate.value.name,
      language: selectedWaTemplate.value.language,
    });
  } else {
    campaignDetails.message = state.message || '';
  }

  if (hasAnyMappings) {
    campaignDetails.variable_mappings = variableMappings.value;
  }

  return campaignDetails;
};

const parseTemplateVariables = () => {
  if (!selectedWaTemplate.value) return;

  let params = selectedWaTemplate.value.params;

  if (!params && selectedWaTemplate.value.components) {
    const bodyComponent = selectedWaTemplate.value.components.find(
      component => component.type === 'BODY'
    );

    if (bodyComponent && bodyComponent.text) {
      const variables = bodyComponent.text.match(/{{([^}]+)}}/g);
      if (variables) {
        params = {};
        variables.forEach(variable => {
          const variableName = variable.replace(/{{|}}/g, '').trim();
          params[variableName] = '';
        });
      }
    }
  }

  if (!params) {
    templateVariables.value = [];
    variableMappings.value = {};
    return;
  }

  const extractedVariables = [];

  Object.keys(params).forEach(key => {
    if (
      params[key] === '' ||
      (typeof params[key] === 'string' &&
        params[key].includes('{{') &&
        params[key].includes('}}'))
    ) {
      extractedVariables.push(key);
    }
  });

  templateVariables.value = extractedVariables;

  variableMappings.value = extractedVariables.reduce((acc, variable) => {
    acc[variable] = '';
    return acc;
  }, {});
};

const toggleTemplateMode = event => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  if (!state.inboxId && !state.useTemplate) {
    useAlert(
      t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEMPLATE.SELECT_INBOX_FIRST')
    );
    return;
  }

  state.useTemplate = !state.useTemplate;
  if (!state.useTemplate) {
    selectedWaTemplate.value = null;
    state.templateParams = null;
  }
};

const handleSubmit = () => {
  reviewAndScheduleValidator.value.$touch();
  if (reviewAndScheduleValidator.value.$invalid) return;

  const campaignDetails = prepareCampaignData();
  emit('submit', campaignDetails);
};

const handlePickerBackOrCancel = () => {
  if (selectedWaTemplate.value) {
    selectedWaTemplate.value = null;
    state.templateParams = null;
  } else {
    toggleTemplateMode();
  }
};

const handleNext = () => {
  if (isCurrentStep(STEPS.INBOX_AND_TEMPLATE)) {
    if (selectedWaTemplate.value && selectedWaTemplate.value.params) {
      parseTemplateVariables();
    }
  }

  if (!canNavigateForward()) return;

  const currentIndex = currentStepIndex.value;
  currentStep.value = activeSteps.value[currentIndex + 1];
};

const pickTemplate = template => {
  selectedWaTemplate.value = template;

  if (template && (template.params || template.components)) {
    parseTemplateVariables();
  }
};

const handleCancel = () => {
  emit('cancel');
};

function restoreFormState() {
  if (props.savedFormState) {
    if (props.savedFormState.state) {
      Object.keys(props.savedFormState.state).forEach(key => {
        if (key in state) {
          state[key] = props.savedFormState.state[key];
        }
      });
    }

    if (props.savedFormState.selectedWaTemplate) {
      selectedWaTemplate.value = props.savedFormState.selectedWaTemplate;
    }

    if (props.savedFormState.csvFileName) {
      csvFileName.value = props.savedFormState.csvFileName;
    }

    if (props.savedFormState.csvHeaders) {
      csvHeaders.value = props.savedFormState.csvHeaders;
    }

    if (props.savedFormState.templateVariables) {
      templateVariables.value = props.savedFormState.templateVariables;
    }

    if (props.savedFormState.variableMappings) {
      variableMappings.value = props.savedFormState.variableMappings;
    }

    if (props.savedFormState.mediaFileName) {
      mediaFileName.value = props.savedFormState.mediaFileName;
    }

    if (props.lastStep !== null) {
      currentStep.value = props.lastStep;
    }
  }
}

const handleFileSelected = ({ file, fileName, headers }) => {
  csvFile.value = file;
  csvFileName.value = fileName;
  csvHeaders.value = headers;
  emit('csvFileSelected', file);
};

const handleRemoveFile = () => {
  csvFile.value = null;
  csvFileName.value = '';
  csvHeaders.value = [];
};

const handleMediaFileSelected = ({ file, fileName }) => {
  mediaFile.value = file;
  mediaFileName.value = fileName;
  emit('mediaFileSelected', file);
};

const handleRemoveMediaFile = () => {
  mediaFile.value = null;
  mediaFileName.value = '';
};

onMounted(() => {
  restoreFormState();
});

watch(() => props.savedFormState, restoreFormState);

watch(
  () => props.csvErrors,
  newErrors => {
    if (newErrors) {
      currentStep.value = STEPS.CSV_ERRORS;
    }
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit" @click.stop>
    <NameAndCsvStep
      v-if="isCurrentStep(STEPS.NAME_AND_CSV)"
      v-model:title="state.title"
      :csv-file="csvFile"
      :csv-file-name="csvFileName"
      :csv-headers="csvHeaders"
      :validate-csv-file="validateCsvFile"
      :validator="nameAndCsvValidator"
      :total-steps="totalSteps"
      :current-step="currentStep"
      :title-error="formErrors.title"
      @file-selected="handleFileSelected"
      @remove-file="handleRemoveFile"
      @next="handleNext"
    />

    <InboxAndTemplateStep
      v-if="isCurrentStep(STEPS.INBOX_AND_TEMPLATE)"
      v-model:inbox-id="state.inboxId"
      v-model:team-id="state.teamId"
      :selected-wa-template="selectedWaTemplate"
      :use-template="state.useTemplate"
      :inbox-options="inboxOptions"
      :team-options="teamOptions"
      :validator="inboxAndTemplateValidator"
      :total-steps="totalSteps"
      :current-step="currentStep"
      :inbox-error="formErrors.inbox"
      @toggle-template-mode="toggleTemplateMode"
      @pick-template="pickTemplate"
      @picker-back-or-cancel="handlePickerBackOrCancel"
      @back="handleBack"
      @next="handleNext"
    />

    <MediaUploadStep
      v-if="isCurrentStep(STEPS.MEDIA_UPLOAD)"
      :media-file="mediaFile"
      :media-file-name="mediaFileName"
      :media-header-format="getMediaHeaderFormat"
      :total-steps="totalSteps"
      :current-step="currentStep"
      @media-file-selected="handleMediaFileSelected"
      @remove-media-file="handleRemoveMediaFile"
      @back="handleBack"
      @next="handleNext"
    />

    <VariableMappingStep
      v-if="isCurrentStep(STEPS.VARIABLE_MAPPING)"
      v-model:variable-mappings="variableMappings"
      :template-variables="templateVariables"
      :csv-headers="csvHeaders"
      :total-steps="totalSteps"
      :current-step="currentStep"
      @back="handleBack"
      @next="handleNext"
    />

    <ReviewAndScheduleStep
      v-if="isCurrentStep(STEPS.REVIEW_AND_SCHEDULE)"
      v-model:scheduled-at="state.scheduledAt"
      :title="state.title"
      :inbox-id="state.inboxId"
      :team-id="state.teamId"
      :csv-file-name="csvFileName"
      :template-variables="templateVariables"
      :variable-mappings="variableMappings"
      :inbox-options="inboxOptions"
      :team-options="teamOptions"
      :validator="reviewAndScheduleValidator"
      :is-creating="isCreating"
      :total-steps="totalSteps"
      :current-step="currentStep"
      :scheduled-at-error="formErrors.scheduledAt"
      @back="handleBack"
      @submit="handleSubmit"
    />

    <CsvErrorsStep
      v-if="isCurrentStep(STEPS.CSV_ERRORS)"
      :csv-errors="csvErrors"
      @cancel="handleCancel"
    />
  </form>
</template>
